<template>
  <BaseModal
    :show="isVisible"
    :hide-close-button="true"
    container-class="frontend-update-modal"
    mask-class="frontend-update-modal-mask"
    wrapper-class="frontend-update-modal-wrapper"
    @close="preventClose"
  >
    <template v-slot:content>
      <div class="frontend-update-modal__content" role="status" aria-live="polite">
        <div class="frontend-update-modal__heading-row flex items-start justify-between gap-3">
          <h3 class="text-base-h6 black-2-color semibold-font m-0">
            {{ $t("frontend_update.title") }}
          </h3>
          <span class="frontend-update-modal__counter text-base-small">
            {{ secondsLeft }}s
          </span>
        </div>

        <p class="text-base-small grey-4-color frontend-update-modal__message">
            {{ $t("frontend_update.message", { seconds: secondsLeft }) }}
        </p>

        <div class="frontend-update-modal__footer flex items-center gap-3">
          <div class="frontend-update-modal__progress-block">
            <div class="frontend-update-modal__progress-track">
              <div
                class="frontend-update-modal__progress-value"
                :style="{ width: progressWidth }"
              ></div>
            </div>
            <div class="text-base-small-2 grey-4-color mt-2">
              {{ secondsLeft }}s
            </div>
          </div>

          <BaseButton
            size="sm"
            variant="primary"
            color="green-5"
            class="frontend-update-modal__action"
            @click="reloadNow"
          >
            {{ $t("frontend_update.button") }}
          </BaseButton>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script>
const AUTO_RELOAD_SECONDS = 30;

export default {
  name: "FrontendUpdateNotice",
  data() {
    return {
      isVisible: false,
      secondsLeft: AUTO_RELOAD_SECONDS,
      timerId: null
    };
  },
  computed: {
    reloadNoticeToken() {
      return this.$store.state.frontendUpdate.reloadNoticeToken;
    },
    progressWidth() {
      const percent = (this.secondsLeft / AUTO_RELOAD_SECONDS) * 100;

      return `${Math.max(percent, 0)}%`;
    }
  },
  watch: {
    reloadNoticeToken() {
      this.showNotice();
    }
  },
  methods: {
    showNotice() {
      this.clearTimer();
      this.isVisible = true;
      this.secondsLeft = AUTO_RELOAD_SECONDS;
      this.timerId = setInterval(() => {
        if (this.secondsLeft <= 1) {
          this.reloadNow();
          return;
        }
        this.secondsLeft -= 1;
      }, 1000);
    },
    reloadNow() {
      this.clearTimer();
      window.location.reload();
    },
    clearTimer() {
      if (this.timerId) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    }
  },
  beforeUnmount() {
    this.clearTimer();
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/styles/design/mainIncludes";

:deep(.frontend-update-modal-mask.modal-mask) {
  background-color: transparent;
  display: block;
  pointer-events: none;
}

:deep(.frontend-update-modal-wrapper.modal-wrapper) {
  display: block;
}

:deep(.frontend-update-modal.modal-container) {
  position: fixed;
  top: 84px;
  right: 20px;
  left: auto;
  z-index: 10000;
  margin: 0;
  width: min(460px, calc(100vw - 24px));
  max-width: none;
  display: flex;
  flex-direction: column;
  padding: 0;
  border: 1px solid $green-3;
  border-radius: 10px;
  overflow: hidden;
  background: linear-gradient(180deg, rgba(244, 247, 244, 0.98) 0%, rgba(255, 255, 255, 0.98) 100%);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
}

:deep(.frontend-update-modal.modal-container)::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  background: linear-gradient(180deg, $green-5 0%, $green-4 100%);
}

:deep(.frontend-update-modal .modal-header) {
  display: none;
}

.frontend-update-modal__content {
  padding: 14px 14px 14px 18px;
}

.frontend-update-modal__message {
  margin: 8px 0 14px;
  line-height: 20px;
}

.frontend-update-modal__counter {
  flex-shrink: 0;
  border-radius: 999px;
  padding: 4px 10px;
  background: rgba(0, 148, 64, 0.12);
  color: $green-5;
  line-height: 1;
}

.frontend-update-modal__progress-block {
  flex: 1;
}

.frontend-update-modal__progress-track {
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 148, 64, 0.18);
  overflow: hidden;
}

.frontend-update-modal__progress-value {
  height: 100%;
  border-radius: inherit;
  transition: width 0.2s linear;
  background: linear-gradient(90deg, $green-5 0%, $green-4 100%);
}

.frontend-update-modal__action {
  min-width: 142px;
}

:deep(.frontend-update-modal .base-btn) {
  white-space: nowrap;
}

@include maxWidth768 {
  :deep(.frontend-update-modal.modal-container) {
    top: 74px;
    left: 12px;
    right: 12px;
    width: auto;
  }

  .frontend-update-modal__content {
    padding: 12px 12px 12px 16px;
  }

  .frontend-update-modal__footer {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .frontend-update-modal__action {
    min-width: 0;
  }

  :deep(.frontend-update-modal .base-btn) {
    width: 100%;
  }
}
</style>
